import { useState, useEffect } from "react";
import { Baby, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface ChildrenSettingsProps {
  childrenEnabled: boolean;
  childrenCapacityMode: 'full' | 'half' | 'none';
  childrenHelpText: string;
  strollerEnabled: boolean;
  strollerHelpText: string;
  onUpdate: (enabled: boolean, capacityMode: 'full' | 'half' | 'none', helpText?: string) => Promise<void>;
  onUpdateStroller: (enabled: boolean, helpText?: string) => Promise<void>;
  disabled?: boolean;
}

const DEFAULT_HELP_TEXT = 'Indiquez combien de personnes dans votre groupe sont des enfants.';
const DEFAULT_STROLLER_HELP_TEXT = 'Cochez si vous venez avec une poussette.';

export function ChildrenSettings({ 
  childrenEnabled, 
  childrenCapacityMode, 
  childrenHelpText,
  strollerEnabled,
  strollerHelpText,
  onUpdate,
  onUpdateStroller,
  disabled 
}: ChildrenSettingsProps) {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(childrenEnabled);
  const [mode, setMode] = useState<'full' | 'half' | 'none'>(childrenCapacityMode);
  const [helpText, setHelpText] = useState(childrenHelpText || DEFAULT_HELP_TEXT);
  const [strollerOn, setStrollerOn] = useState(strollerEnabled);
  const [strollerText, setStrollerText] = useState(strollerHelpText || DEFAULT_STROLLER_HELP_TEXT);
  const [saving, setSaving] = useState(false);
  const [savingStroller, setSavingStroller] = useState(false);

  useEffect(() => {
    setEnabled(childrenEnabled);
    setMode(childrenCapacityMode);
    setHelpText(childrenHelpText || DEFAULT_HELP_TEXT);
    setStrollerOn(strollerEnabled);
    setStrollerText(strollerHelpText || DEFAULT_STROLLER_HELP_TEXT);
  }, [childrenEnabled, childrenCapacityMode, childrenHelpText, strollerEnabled, strollerHelpText]);

  const hasChanges = enabled !== childrenEnabled || mode !== childrenCapacityMode || helpText !== (childrenHelpText || DEFAULT_HELP_TEXT);
  const hasStrollerChanges = strollerOn !== strollerEnabled || strollerText !== (strollerHelpText || DEFAULT_STROLLER_HELP_TEXT);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(enabled, mode, helpText);
      toast({
        title: "Paramètres enfants mis à jour",
        description: enabled 
          ? "Les clients pourront indiquer le nombre d'enfants lors de la réservation."
          : "L'option enfants a été désactivée.",
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour les paramètres.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStroller = async () => {
    setSavingStroller(true);
    try {
      await onUpdateStroller(strollerOn, strollerText);
      toast({
        title: "Paramètres poussette mis à jour",
        description: strollerOn 
          ? "Les clients pourront indiquer s'ils viennent avec une poussette."
          : "L'option poussette a été désactivée.",
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour les paramètres poussette.",
        variant: "destructive",
      });
    } finally {
      setSavingStroller(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Baby className="w-5 h-5" />
          Réservations avec enfants
        </CardTitle>
        <CardDescription>
          Permettre aux clients d'indiquer s'ils viennent avec des enfants
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="children-enabled">Activer l'option enfants</Label>
            <p className="text-xs text-muted-foreground">
              Un champ "Nombre d'enfants" apparaîtra dans le formulaire de réservation
            </p>
          </div>
          <Switch
            id="children-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            disabled={disabled}
          />
        </div>

        {enabled && (
          <div className="space-y-3 pt-2 border-t">
            <Label className="text-sm font-medium">
              Impact sur la capacité des tables
            </Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as 'full' | 'half' | 'none')}
              className="space-y-2"
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="full" id="mode-full" className="mt-0.5" />
                <div className="space-y-1">
                  <Label htmlFor="mode-full" className="font-medium cursor-pointer">
                    Un enfant = une place
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Chaque enfant occupe une place. Ex : 2 adultes + 2 enfants nécessitent une table de 4.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="half" id="mode-half" className="mt-0.5" />
                <div className="space-y-1">
                  <Label htmlFor="mode-half" className="font-medium cursor-pointer">
                    Un enfant = une demi-place
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Deux enfants occupent une place. Ex : 2 adultes + 2 enfants nécessitent une table de 3.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="none" id="mode-none" className="mt-0.5" />
                <div className="space-y-1">
                  <Label htmlFor="mode-none" className="font-medium cursor-pointer">
                    Les enfants ne comptent pas
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Les enfants n'affectent pas la capacité. Ex : 2 adultes + 3 enfants nécessitent une table de 2.
                  </p>
                </div>
              </div>
            </RadioGroup>

            <div className="space-y-2 pt-3 border-t">
              <Label htmlFor="children-help-text" className="text-sm font-medium">
                Texte d'aide affiché au client
              </Label>
              <Input
                id="children-help-text"
                value={helpText}
                onChange={(e) => setHelpText(e.target.value)}
                placeholder={DEFAULT_HELP_TEXT}
                disabled={disabled}
              />
              <p className="text-xs text-muted-foreground">
                Ce texte apparaît sous le sélecteur d'enfants dans le formulaire de réservation.
              </p>
            </div>
          </div>
        )}

        {/* Stroller settings - independent of children toggle */}
        <div className="space-y-3 pt-3 border-t">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="stroller-enabled">Activer l'option poussette</Label>
              <p className="text-xs text-muted-foreground">
                Les clients pourront indiquer s'ils viennent avec une poussette
              </p>
            </div>
            <Switch
              id="stroller-enabled"
              checked={strollerOn}
              onCheckedChange={setStrollerOn}
              disabled={disabled}
            />
          </div>

          {strollerOn && (
            <div className="space-y-2 pt-2 border-t">
              <Label htmlFor="stroller-help-text" className="text-sm font-medium">
                Texte d'aide poussette
              </Label>
              <Input
                id="stroller-help-text"
                value={strollerText}
                onChange={(e) => setStrollerText(e.target.value)}
                placeholder={DEFAULT_STROLLER_HELP_TEXT}
                disabled={disabled}
              />
            </div>
          )}

          {hasStrollerChanges && (
            <Button
              onClick={handleSaveStroller}
              disabled={savingStroller || disabled}
              className="w-full"
            >
              {savingStroller ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                "Sauvegarder poussette"
              )}
            </Button>
          )}
        </div>

        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={saving || disabled}
            className="w-full"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              "Sauvegarder"
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
